import { Link } from "react-router-dom";
import NetworkGraph from "./components/NetworkGraph";
import Reveal from "./components/Reveal";
import imgEniato from "./assets/Eniato.jpg"
import imgGrupo55 from "./assets/logo-grupo-55-circulo.png"
import imgFactura from "./assets/logo-factura.png"

/* =========================================================================
   Cases
   Página de cases de clientes — segue a mesma estrutura das demais páginas
   de serviço (page-hero no topo + seções .section). Mostra, para cada
   cliente: uma foto/avatar (foto real quando existir, ou iniciais como
   fallback), o desafio, a solução aplicada, números de impacto e um
   depoimento.

   TUDO aqui é conteúdo de exemplo (nomes de contato, cargos, números e
   depoimentos são fictícios) — a ideia é já deixar a página funcionando
   com a estrutura visual pronta para você substituir pelos dados reais
   depois. Basta editar o array CASES abaixo.

   Para usar uma foto real de um cliente, importe a imagem no topo do
   arquivo (como "imgEniato" abaixo) e passe ela no campo "imagem" do
   case correspondente. Sem o campo "imagem", o card cai automaticamente
   para o avatar de iniciais.

   Uso: <Cases /> — componente independente. Sugestão de rota: "/cases".
   ========================================================================= */

const CASES = [
  {
    id: "eniato",
    name: "Eniato Contabilidade",
    segment: "Contabilidade",
    initials: "EC",
    color: "#2a4d94",
    imagem: imgEniato,
    challenge:
      "Escritório com dezenas de estações de trabalho e prazos fiscais apertados — qualquer parada de sistema no fechamento do mês significava risco de multa e atraso na entrega aos clientes.",
    solution:
      "Implantamos Help Desk com atendimento remoto e presencial, RMM para monitoramento contínuo das máquinas e rotina de backup automatizado dos arquivos contábeis e certificados digitais.",
    stats: [
      { value: "-60%", label: "Tempo médio de resposta" },
      { value: "0", label: "Perdas de dados desde a implantação" },
      { value: "+35", label: "Estações monitoradas" },
    ],
    quote:
      "Desde que fechamos com a sepolTECH, paramos de perder tempo com computador travado no meio do fechamento. O suporte responde rápido e resolve.",
    personName: "Nome do Contato",
    personRole: "Sócio-diretor · Eniato Contabilidade",
  },
  {
    id: "grupo55",
    name: "Grupo 55",
    segment: "Varejo · Multiunidades",
    initials: "G5",
    color: "#a1355c",
    imagem: imgGrupo55,
    challenge:
      "Várias unidades espalhadas, cada uma com um jeito diferente de lidar com TI — chamados demoravam para ser atendidos e não existia padrão de software entre as lojas.",
    solution:
      "Centralizamos o suporte técnico com SLA definido para todas as unidades, padronizamos o pacote de softwares instalados e passamos a registrar todo chamado com histórico e relatório mensal.",
    stats: [
      { value: "+8", label: "Unidades padronizadas" },
      { value: "-50%", label: "Chamados recorrentes" },
      { value: "98%", label: "Chamados resolvidos remotamente" },
    ],
    quote:
      "Hoje sabemos exatamente o que está acontecendo em cada loja. Antes cada unidade resolvia do seu jeito, agora é tudo centralizado e muito mais rápido.",
    personName: "Nome do Contato",
    personRole: "Gerente de TI · Grupo 55",
  },
  {
    id: "grupofactura",
    name: "Grupo Factura",
    segment: "Serviços Financeiros",
    initials: "GF",
    imagem: imgFactura,
    color: "#d1241c",
    challenge:
      "Dados financeiros sensíveis exigiam um ambiente mais seguro, com resposta rápida em caso de incidente e controle real sobre antivírus e firewall em todos os endpoints.",
    solution:
      "Implantamos gestão de endpoint (antivírus e firewall), backup completo com rotina diária e suporte prioritário de Nível 2 e 3 para qualquer questão de segurança ou infraestrutura.",
    stats: [
      { value: "100%", label: "Endpoints protegidos" },
      { value: "-70%", label: "Incidentes de segurança" },
      { value: "~15min", label: "SLA médio de atendimento" },
    ],
    quote:
      "A segurança dos nossos dados é inegociável. Com a sepolTECH monitorando tudo, temos muito mais tranquilidade no dia a dia.",
    personName: "Nome do Contato",
    personRole: "Diretor Financeiro · Grupo Factura",
  },
];

const HIGHLIGHTS = [
  { value: "3", label: "Empresas atendidas em destaque" },
  { value: "-60%", label: "Redução média no tempo de resposta" },
  { value: "98%", label: "Satisfação nos chamados" },
  { value: "24/7", label: "Monitoramento contínuo" },
];

function CompanyAvatar({ initials, color, size = 64 }) {
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, color-mix(in srgb, ${color} 70%, black) 100%)`,
        color: "#fff",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: `${Math.round(size * 0.32)}px`,
        letterSpacing: "-.02em",
        boxShadow: `0 10px 24px ${color}40`,
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

/* Foto/"profile foto" da empresa. Usa a imagem real quando o case tem o
   campo "imagem" preenchido; caso contrário, cai para o avatar de
   iniciais (CompanyAvatar) — é por isso que Grupo 55 e Grupo Factura, que
   ainda não têm foto, aparecem com as iniciais normalmente. */
function CompanyLogo({ c, size = 64 }) {
  if (c.imagem) {
    return (
      <img
        className="img-cases"
        src={c.imagem}
        alt={c.name}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          boxShadow: `0 0 0 4px ${c.color}1A, 0 0 24px ${c.color}66`,
        }}
      />
    );
  }

  return <CompanyAvatar initials={c.initials} color={c.color} size={size} />;
}

function PersonAvatar({ name, color }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: `${color}1F`,
        color,
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: "14px",
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

export default function Cases() {
  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <span>Cases</span>
          </div>
          <div className="tag animate-up animate-up--d1">
            CASES DE SUCESSO · RESULTADOS REAIS
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Cases de Clientes
            <br />
            <span className="text-blue">
              Resultados que falam por si
            </span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Empresas que confiaram na sepolTECH para cuidar da sua infraestrutura de TI — e
            os números e depoimentos que mostram o impacto real disso no dia a dia delas.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Quero resultados assim →
            </Link>
            <Link to="/servicos/servicos" className="btn btn-outline">
              Ver todos os serviços
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ NÚMEROS EM DESTAQUE ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="grid-2col-stats" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              {HIGHLIGHTS.map((h) => (
                <div className="stat-card" key={h.label}>
                  <div className="stat-card__value">{h.value}</div>
                  <div className="stat-card__label">{h.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ CASES ═══════════════ */}
      <section className="section section--alt">
        <div className="container">
          <Reveal className="text-center" style={{ marginBottom: "48px" }}>
            <div className="tag">Cases</div>
            <h2 className="section-title">Quem já colhe os resultados</h2>
            <p className="section-subtitle" style={{ margin: "0 auto" }}>
              Um retrato de como a sepolTECH atua no dia a dia de empresas de diferentes
              segmentos.
            </p>
          </Reveal>

          <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
            {CASES.map((c, index) => (
              <Reveal delay={index * 0.1} key={c.id}>
                <div className="card" style={{ padding: "40px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "18px", marginBottom: "24px", flexWrap: "wrap" }}>
                    <CompanyLogo c={c} />
                    <div>
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 700,
                          fontSize: "20px",
                          color: "var(--text-primary)",
                          marginBottom: "6px",
                        }}
                      >
                        {c.name}
                      </h3>
                      <span
                        className="tag"
                        style={{
                          margin: 0,
                          color: c.color,
                          background: `${c.color}1A`,
                          borderColor: `${c.color}40`,
                        }}
                      >
                        {c.segment}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "28px" }}>
                    <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: 1.7 }}>
                      <strong style={{ color: "var(--text-primary)" }}>Desafio: </strong>
                      {c.challenge}
                    </p>
                    <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: 1.7 }}>
                      <strong style={{ color: "var(--text-primary)" }}>Solução: </strong>
                      {c.solution}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "16px",
                      marginBottom: "28px",
                    }}
                  >
                    {c.stats.map((s) => (
                      <div key={s.label} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: "28px",
                            fontWeight: 700,
                            color: c.color,
                            lineHeight: 1,
                          }}
                        >
                          {s.value}
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "6px" }}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "14px",
                      alignItems: "flex-start",
                      background: "var(--bg-alt)",
                      borderRadius: "var(--radius-md)",
                      padding: "20px",
                    }}
                  >
                    <PersonAvatar name={c.personName} color={c.color} />
                    <div>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "var(--text-secondary)",
                          lineHeight: 1.65,
                          fontStyle: "italic",
                          marginBottom: "10px",
                        }}
                      >
                        "{c.quote}"
                      </p>
                      <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                        {c.personName}
                      </strong>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{c.personRole}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              O próximo case
              <br />
              pode ser o da sua empresa
            </h2>
            <p className="cta-banner__sub">
              Fale com a gente e veja como a sepolTECH pode cuidar da TI do seu negócio.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Falar com a equipe →
              </Link>
              <Link to="/servicos/servicos" className="btn btn-outline">
                Ver todos os serviços
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
